import { FormBuilder } from '@/components/formBuilder';
import { useInterviewDetailsConfig } from './useInterviewDetailsConfig';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormikProps, setNestedObjectValues } from 'formik';
import {
  useMeetingLocationConfig,
  useNoteTakerConfig,
  useOfficeConfig,
  useInterviewerPoolConfig,
  useSharedCalendarConfig,
} from '@/formConfigs';
import type { StepFormApi } from '@/hooks/useStepValidation';
import type { InterviewActivityStepProps } from '../AddInterviewActivity.interface';
import { useInterviewStageManager } from '@/pages/ats-job-creation/tabs/interview-plan/stage/stage-manager';

function findFirstLeafFormikErrorKey(
  errors: Record<string, unknown> | undefined
): string | null {
  if (!errors || typeof errors !== 'object') {
    return null;
  }
  for (const [k, v] of Object.entries(errors)) {
    if (typeof v === 'string' && v) {
      return k;
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const nested = findFirstLeafFormikErrorKey(v as Record<string, unknown>);
      if (nested) {
        return `${k}.${nested}`;
      }
    }
  }
  return null;
}

/** Scroll the Details step viewport to the first invalid field so "Next" failures are obvious. */
function scrollInterviewDetailsToFirstError(errors: Record<string, unknown>) {
  const fullKey = findFirstLeafFormikErrorKey(errors);
  if (!fullKey) {
    return;
  }
  const root = fullKey.split('.')[0];
  const selectors = [fullKey, root].flatMap((key) => {
    let escaped = key;
    try {
      escaped =
        typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
          ? CSS.escape(key)
          : key;
    } catch {
      escaped = key;
    }
    return [`[name="${key}"]`, `#${escaped}`];
  });

  window.requestAnimationFrame(() => {
    const scope = document.querySelector('[data-interview-details-step]');
    for (const sel of selectors) {
      try {
        const el =
          (scope?.querySelector(sel) as HTMLElement | null) ??
          (document.querySelector(sel) as HTMLElement | null);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          break;
        }
      } catch {
        // ignore invalid selector
      }
    }
  });
}

export const InterviewActivityDetails = memo(
  forwardRef<StepFormApi, InterviewActivityStepProps>(
    ({ payloadRef, selectedActivity, prefillVersion = 0 }, ref) => {
      const isHackerRank = selectedActivity === 'hackerrank_interview';
      const {
        interviewerWithVariables,
        interviewType,
        interviewerSelectionOptions,
        interviewers,
      } = useInterviewDetailsConfig();

      const { activityAction } = useInterviewStageManager();
      const { stage = {} } = activityAction as { stage: { name?: string } };

      const noteTakerConfig = useNoteTakerConfig();
      const { formConfig: meetingLocationConfig, defaultLocation } =
        useMeetingLocationConfig({
          isHackerRank,
        });
      const officeConfig = useOfficeConfig();
      const interviewerPoolConfig = useInterviewerPoolConfig();
      const { formConfig: sharedCalendarConfig } = useSharedCalendarConfig({});

      const participantDefaultOptions = useMemo(() => {
        const raw = (
          payloadRef.current.details as Record<string, unknown> | undefined
        )?.participant_default_options;
        return Array.isArray(raw) ? raw : [];
        // eslint-disable-next-line react-hooks/exhaustive-deps -- read `payloadRef` when `prefillVersion` bumps after GET map
      }, [prefillVersion]);

      const formConfig = useMemo(
        () =>
          [
            { type: 'sectionLabel', label: 'Interview Details' },
            interviewerWithVariables,
            interviewType,
            interviewerSelectionOptions,
            {
              ...interviewers,
              ...(participantDefaultOptions.length > 0
                ? { defaultOptions: participantDefaultOptions }
                : {}),
            },
            interviewerPoolConfig,
            { type: 'sectionLabel', label: 'Schedule details' },
            ...sharedCalendarConfig,
            {
              name: 'duration',
              label: 'Duration',
              type: 'numberStepper',
              step: 15,
              min: 15,
              max: 600,
              unitLabel: 'mins',
            },
            {
              type: 'switch',
              name: 'calendar_visibility',
              label: 'Make the interview private',
              subLabel: 'Hide calendar event details from non-attendees',
            },
            meetingLocationConfig,
            officeConfig,
            {
              type: 'select',
              name: 'host',
              label: 'Host',
              options: [
                { label: 'Organizer', value: 'organizer' },
                {
                  label: 'One of the interviewers',
                  value: 'one_of_the_interviewers',
                },
              ],
            },
            noteTakerConfig,
          ].filter(Boolean),
        [
          interviewerWithVariables,
          interviewType,
          interviewerSelectionOptions,
          interviewers,
          participantDefaultOptions,
          interviewerPoolConfig,
          sharedCalendarConfig,
          meetingLocationConfig,
          officeConfig,
          noteTakerConfig,
        ]
      );

      const initialValues = useMemo(() => {
        const rawDetails = (payloadRef.current.details ?? {}) as Record<
          string,
          unknown
        >;
        const { participant_default_options: _omit, ...detailsForForm } =
          rawDetails;
        return {
          interviewName: stage?.name ? `Interview - ${stage?.name}` : '',
          interview_type: 'one_on_one',
          interviewTypeRadio: 'manual_selection',
          participant_ids: [],
          interview_pool_id: '',
          duration: 30,
          schedule_selection: 'manual',
          calendar: 'organizer_calendar',
          calendar_visibility: false,
          office: '',
          host: 'organizer',
          location: defaultLocation,
          enable_note_taker: false,
          ext_interview_template_id: null,
          ...detailsForForm,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- read payloadRef only when prefillVersion bumps
      }, [defaultLocation, stage, prefillVersion]);

      const onSubmit = () => {};
      const formRef = useRef<FormikProps<Record<string, unknown>>>(null);

      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const f = formRef.current;
            if (!f) {
              return true;
            }
            const errors = await f.validateForm();
            if (Object.keys(errors).length > 0) {
              f.setTouched(setNestedObjectValues(f.values, true));
              scrollInterviewDetailsToFirstError(
                errors as Record<string, unknown>
              );
              return false;
            }
            return true;
          },
          syncToPayload: () => {
            const f = formRef.current;
            if (f) {
              const keep = (
                payloadRef.current.details as
                  | Record<string, unknown>
                  | undefined
              )?.participant_default_options;
              payloadRef.current.details = {
                ...f.values,
                ...(Array.isArray(keep) && keep.length > 0
                  ? { participant_default_options: keep }
                  : {}),
              };
            }
          },
        }),
        [payloadRef]
      );

      return (
        <Flex
          data-interview-details-step
          justifyContent="center"
          w="100%"
          minH={0}
          flex="1"
          overflowY="auto"
          key={String(prefillVersion)}
        >
          <Flex w="560px" maxW="100%" flexDir="column" pb="16">
            <FormBuilder
              formRef={formRef}
              config={formConfig}
              initialValues={initialValues}
              formContainerProps={{ width: '100%' }}
              onSubmit={onSubmit}
              useGridStyles
            />
          </Flex>
        </Flex>
      );
    }
  )
);

InterviewActivityDetails.displayName = 'InterviewActivityDetails';
